import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ActionButton } from '@adobe/react-spectrum';
import LinkOut from '@spectrum-icons/workflow/LinkOut';

import { getPrettyCategoryLabel } from '../../../data/CategoryPrettyDisplayUtils';
import { useOpenCardInfo } from '../../common/cardInfoDialog/useOpenCardInfo';
import { ExpandableRow } from '../../common/expandableRow/ExpandableRow';
import { useExclusiveExpansion } from '../../common/expandableRow/useExclusiveExpansion';
import './CombosPane.css';

// ---------- Wincon / enabler classification ---------------------------------
// Kept aligned with backend DetailsCombos.js. A combo must carry one of the
// `DIRECT_WINCON_CATEGORIES`, OR both infinite-mana + infinite-draw, to count
// as a real wincon; enablers just need to look like enablers. Only the
// WINCON/ENABLER split drives our top-level header chips — the specific
// sub-categories surface as per-row badges instead of an extra nav layer.

const WINCON_CATEGORY_IDS = new Set([
    'INFINITE-DAMAGE',
    'INFINITE-LIFELOSS',
    'INFINITE-COMBAT',
    'OPPONENTS-LOCKED',
    'INFINITE-MILL',
    'INFINITE-EXILE',
    'INFINITE-TOKENS-WITH-HASTE',
    'INFINITE-TOKENS',
    'INFINITE-COUNTERS',
    'INFINITE-TURNS',
    'DRAW-MANA',
    'WIN-GAME',
]);

const DIRECT_WINCON_CATEGORIES = new Set([
    'INFINITE-DAMAGE',
    'INFINITE-LIFELOSS',
    'INFINITE-COMBAT',
    'INFINITE-TOKENS-WITH-HASTE',
    'INFINITE-TOKENS',
    'OPPONENTS-LOCKED',
    'INFINITE-COUNTERS',
    'INFINITE-TURNS',
    'INFINITE-MILL',
    'INFINITE-EXILE',
    'WIN-GAME',
]);

const getCategoryId = (cat) => {
    if (typeof cat === 'string') return cat.toUpperCase();
    if (cat && typeof cat === 'object' && cat.id) return cat.id.toUpperCase();
    return null;
};

function isValidWincon(combo) {
    const cats = (combo?.categories || []).map(getCategoryId).filter(Boolean);
    for (const c of cats) {
        if (DIRECT_WINCON_CATEGORIES.has(c)) return { valid: true, hasDrawMana: false };
    }
    if (cats.includes('INFINITE-DRAW') && cats.includes('INFINITE-MANA')) {
        return { valid: true, hasDrawMana: true };
    }
    return { valid: false, hasDrawMana: false };
}

function classifyComboType(combo) {
    const raw = (!combo.type || combo.type === '' ? 'enabler' : combo.type).toUpperCase();
    if (raw === 'WINCON') return isValidWincon(combo).valid ? 'WINCON' : null;
    if (raw === 'LOCK') return 'LOCK';
    return 'ENABLER';
}

// ---------- Card resolution (identical logic to previous pane) -------------

function resolveComboCard(cards, memberId) {
    if (!cards) return null;
    if (cards[memberId]) return cards[memberId];
    let retCard = null;
    Object.keys(cards).forEach((cardId) => {
        const card = cards[cardId];
        if (card?.containerId !== memberId) return;
        if (card.isFrontFace) {
            retCard = card;
        } else if (!retCard) {
            if (new RegExp(`_${memberId}`).test(cardId)) retCard = card;
            else if (new RegExp(`${memberId}_back`).test(cardId)) retCard = card;
        }
    });
    return retCard;
}

function hydrateCombo(combo, cards) {
    const members = (combo?.cards || []).map((id) => resolveComboCard(cards, id)).filter(Boolean);
    const type = classifyComboType(combo);
    return { ...combo, __members: members, __type: type };
}

// ---------- Display helpers ------------------------------------------------

const TYPE_META = {
    WINCON: { label: 'Wincon', tone: 'alert' },
    ENABLER: { label: 'Enabler', tone: 'gold' },
    LOCK: { label: 'Lock', tone: 'cool' },
};

const FILTERS = [
    { key: 'ALL', label: 'All' },
    { key: 'WINCON', label: 'Wincons' },
    { key: 'ENABLER', label: 'Enablers' },
    { key: 'LOCK', label: 'Locks' },
];

function formatComplexity(value) {
    if (value == null || Number.isNaN(value)) return '—';
    return `${(value * 100).toFixed(0)}%`;
}

function complexityTone(value) {
    if (value == null || Number.isNaN(value)) return 'neutral';
    if (value >= 0.65) return 'easy';
    if (value >= 0.4) return 'moderate';
    return 'hard';
}

// ---------- Pane -----------------------------------------------------------

export function CombosPane() {
    const deck = useSelector((s) => s.deck.deck);
    const cards = useSelector((s) => s.deck.cards);
    const openCardInfo = useOpenCardInfo();

    const [filter, setFilter] = useState('ALL');
    const { expandedId, toggle: toggleExpanded } = useExclusiveExpansion();

    const combos = useMemo(() => {
        const list = deck?.details?.combos?.list || [];
        return list
            .map((c) => hydrateCombo(c, cards))
            .filter((c) => c.__type !== null)
            .sort((a, b) => {
                // Highest score first, then most-complex steps, then shortest cards.
                const scoreDiff = (b?.score || 0) - (a?.score || 0);
                if (scoreDiff !== 0) return scoreDiff;
                const bx = b?.complexity?.bias?.final ?? 0;
                const ax = a?.complexity?.bias?.final ?? 0;
                return bx - ax;
            });
    }, [deck, cards]);

    const stats = useMemo(() => computeComboStats(combos), [combos]);

    const filteredCombos = useMemo(() => {
        if (filter === 'ALL') return combos;
        return combos.filter((c) => c.__type === filter);
    }, [combos, filter]);

    return (
        <div className="combosPane">
            <CombosHero stats={stats} deckScore={deck?.details?.combos?.score} />

            <div className="combosFilterRow" role="tablist" aria-label="Combo filter">
                {FILTERS.map((f) => {
                    const count =
                        f.key === 'ALL' ? combos.length : combos.filter((c) => c.__type === f.key).length;
                    const disabled = f.key !== 'ALL' && count === 0;
                    return (
                        <button
                            key={f.key}
                            type="button"
                            className={`combosFilterBtn ${filter === f.key ? 'is-active' : ''}`}
                            onClick={() => !disabled && setFilter(f.key)}
                            disabled={disabled}
                            role="tab"
                            aria-selected={filter === f.key}
                        >
                            <span className="combosFilterBtnLabel">{f.label}</span>
                            <span className="combosFilterBtnCount">{count}</span>
                        </button>
                    );
                })}
            </div>

            {filteredCombos.length === 0 ? (
                <div className="combosEmpty">
                    <span className="combosEmptyEyebrow">No combos</span>
                    <span className="combosEmptyBody">
                        {combos.length === 0
                            ? 'This deck has no detected combos. Pure value decks often show up here — or the card-pair patterns simply haven\u2019t been indexed yet.'
                            : `No ${filter.toLowerCase()} combos match. Try another filter.`}
                    </span>
                </div>
            ) : (
                <div className="combosList">
                    {filteredCombos.map((combo) => {
                        const rowKey = `combo:${combo.id}`;
                        return (
                            <ComboRow
                                key={combo.id}
                                combo={combo}
                                expanded={expandedId === rowKey}
                                onToggle={() => toggleExpanded(rowKey)}
                                onSelectCard={openCardInfo}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ---------- Hero -----------------------------------------------------------

function CombosHero({ stats, deckScore }) {
    const { total, counts, tagCounts, linchpins, avgComplexity, avgExtraCmc } = stats;
    return (
        <section className="combosHero">
            <div className="combosHeroTop">
                <span className="combosHeroEyebrow">Combo profile</span>
                {deckScore != null && (
                    <span className="combosHeroDeckScore">
                        <span className="combosHeroDeckScoreLabel">Combo score</span>
                        <span className="combosHeroDeckScoreValue">{Number(deckScore).toFixed(0)}</span>
                    </span>
                )}
            </div>

            <div className="combosHeroMetrics">
                <HeroMetric label="Combos" value={total} />
                <HeroMetric label="Wincons" value={counts.WINCON || 0} tone="alert" />
                <HeroMetric label="Enablers" value={counts.ENABLER || 0} tone="gold" />
                <HeroMetric label="Locks" value={counts.LOCK || 0} tone="cool" />
                <HeroMetric
                    label="Avg complexity"
                    value={avgComplexity != null ? formatComplexity(avgComplexity) : '—'}
                    hint="Higher = easier to assemble (backend bias)."
                />
                <HeroMetric
                    label="Avg extra mana"
                    value={avgExtraCmc != null ? `+${avgExtraCmc.toFixed(1)}` : '—'}
                    hint="Mana needed beyond the cards in the combo."
                />
            </div>

            {tagCounts.length > 0 && (
                <div className="combosHeroRow">
                    <span className="combosHeroRowLabel">Effect mix</span>
                    <div className="combosHeroChipRow">
                        {tagCounts.slice(0, 10).map(({ tag, count }) => (
                            <span key={tag} className="combosHeroEffectChip" title={`${count} combo${count === 1 ? '' : 's'}`}>
                                <span className="combosHeroEffectChipLabel">{getPrettyCategoryLabel(tag)}</span>
                                <span className="combosHeroEffectChipCount">{count}</span>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {linchpins.length > 0 && (
                <div className="combosHeroRow">
                    <span className="combosHeroRowLabel">Linchpin cards</span>
                    <div className="combosHeroChipRow">
                        {linchpins.slice(0, 8).map(({ card, count }) => (
                            <span key={card?.id || card?.name} className="combosHeroLinchpin" title={`Appears in ${count} combo${count === 1 ? '' : 's'}`}>
                                {card?.imageUri ? (
                                    <img src={card.imageUri} alt={card.name} className="combosHeroLinchpinThumb" loading="lazy" />
                                ) : (
                                    <span className="combosHeroLinchpinThumb combosHeroLinchpinThumb--empty" />
                                )}
                                <span className="combosHeroLinchpinName">{card?.name || 'Unknown'}</span>
                                <span className="combosHeroLinchpinCount">×{count}</span>
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}

function HeroMetric({ label, value, hint, tone }) {
    return (
        <div className={`combosHeroMetric${tone ? ` combosHeroMetric--${tone}` : ''}`} title={hint}>
            <span className="combosHeroMetricLabel">{label}</span>
            <span className="combosHeroMetricValue">{value}</span>
        </div>
    );
}

// ---------- Per-combo row --------------------------------------------------

function ComboRow({ combo, expanded, onToggle, onSelectCard }) {
    const type = combo.__type;
    const typeMeta = TYPE_META[type] || { label: 'Combo', tone: 'gold' };
    const cats = (combo.categories || []).map(getCategoryId).filter(Boolean);
    const cx = combo?.complexity || {};
    const cxFinal = cx?.bias?.final;
    const cxTone = complexityTone(cxFinal);
    const extraCmc = cx?.additionalCmcValue;
    const flags = buildComboFlags(combo);
    const score = Number(combo?.score || 0);

    const sortedMembers = (combo.__members || []).slice().sort((a, b) =>
        (a?.name || '').localeCompare(b?.name || '')
    );

    const header = (
        <>
            <div className="comboCardHeaderMain">
                <div className="comboCardCardChips">
                    {sortedMembers.map((card, idx) => (
                        <ComboCardChip
                            key={`${combo.id}_${card?.id || idx}`}
                            card={card}
                            onSelectCard={onSelectCard}
                        />
                    ))}
                </div>
                <div className="comboCardTagRow">
                    <span className={`comboTypeTag comboTypeTag--${typeMeta.tone}`}>{typeMeta.label}</span>
                    {cats.slice(0, 6).map((cat) => (
                        <span key={cat} className="comboEffectTag">
                            {getPrettyCategoryLabel(cat)}
                        </span>
                    ))}
                    {flags.map((f) => (
                        <span key={f.key} className={`comboFlag comboFlag--${f.tone}`} title={f.hint}>
                            {f.label}
                        </span>
                    ))}
                </div>
            </div>
            <div className="comboCardHeaderMeta">
                <div className="comboCardMetric">
                    <span className="comboCardMetricLabel">Score</span>
                    <span className="comboCardMetricValue">{score.toFixed(0)}</span>
                </div>
                <div className={`comboCardMetric comboCardMetric--${cxTone}`}>
                    <span className="comboCardMetricLabel">Complexity</span>
                    <span className="comboCardMetricValue">{formatComplexity(cxFinal)}</span>
                </div>
                {extraCmc > 0 && (
                    <div className="comboCardMetric">
                        <span className="comboCardMetricLabel">Extra mana</span>
                        <span className="comboCardMetricValue">+{extraCmc}</span>
                    </div>
                )}
            </div>
        </>
    );

    return (
        <ExpandableRow
            expanded={expanded}
            onToggle={onToggle}
            tone={typeMeta.tone === 'alert' ? 'red' : typeMeta.tone === 'gold' ? 'gold' : 'cool'}
            rowClassName="comboCard"
            bodyClassName="comboCardBody"
            headerClassName="comboCardHeader"
            header={header}
        >
            <ComboCardBody combo={combo} />
        </ExpandableRow>
    );
}

function ComboCardChip({ card, onSelectCard }) {
    if (!card) return null;
    const name = card?.name || card?.id || 'Unknown';
    const handleClick = (e) => {
        e.stopPropagation();
        if (onSelectCard) onSelectCard(card);
    };
    return (
        <span
            className="comboCardChip"
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onSelectCard) onSelectCard(card);
                }
            }}
            title={`View ${name}`}
        >
            {card?.imageUri ? (
                <img src={card.imageUri} alt={name} className="comboCardChipThumb" loading="lazy" />
            ) : (
                <span className="comboCardChipThumb comboCardChipThumb--empty" />
            )}
            <span
                className="comboCardChipName"
                style={{ color: card?.isFrontFace === false ? '#fff49a' : '#ffffff' }}
            >
                {name}
            </span>
        </span>
    );
}

function ComboCardBody({ combo }) {
    const cx = combo?.complexity || {};
    const steps = cx?.steps?.lines || [];
    const results = cx?.results?.lines || [];
    const easy = cx?.easyPrerequisites?.lines || [];
    const notable = cx?.notablePrerequisites?.lines || [];
    const preconditions = cx?.preconditions?.lines || [];
    const sections = cx?.bias?.sections || {};
    const spellbookUri = combo?.spellbookUri;

    return (
        <div className="comboCardBody">
            {(easy.length > 0 || notable.length > 0 || preconditions.length > 0) && (
                <ComboSection title="Prerequisites">
                    {preconditions.length > 0 && (
                        <ComboSubList
                            label="Must be true"
                            tone="alert"
                            lines={preconditions}
                        />
                    )}
                    {notable.length > 0 && (
                        <ComboSubList label="Notable" tone="watch" lines={notable} />
                    )}
                    {easy.length > 0 && (
                        <ComboSubList label="Easy" tone="ok" lines={easy} />
                    )}
                </ComboSection>
            )}

            {steps.length > 0 && (
                <ComboSection title="Step-by-step">
                    <ol className="comboStepList">
                        {steps.map((s, idx) => (
                            <li className="comboStep" key={idx}>
                                <span className="comboStepNum">{idx + 1}</span>
                                <span className="comboStepText">{s.parsed}</span>
                                {s.qualifierCount > 0 && (
                                    <span
                                        className="comboStepQualifier"
                                        title={buildQualifierHint(s.qualifiers)}
                                    >
                                        {buildQualifierLabel(s.qualifiers)}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ol>
                </ComboSection>
            )}

            {results.length > 0 && (
                <ComboSection title="Produces">
                    <ul className="comboResultList">
                        {results.map((r, idx) => (
                            <li className="comboResult" key={idx}>
                                {r.parsed}
                            </li>
                        ))}
                    </ul>
                </ComboSection>
            )}

            {Object.keys(sections).length > 0 && (
                <ComboSection title="Difficulty breakdown" hint="Per-section bias · higher bars = easier">
                    <div className="comboBiasGrid">
                        {Object.keys(sections).map((key) => {
                            const v = Number(sections[key] ?? 0);
                            return (
                                <div className="comboBiasRow" key={key}>
                                    <span className="comboBiasLabel">{humanizeSectionKey(key)}</span>
                                    <div className="comboBiasTrack">
                                        <div
                                            className="comboBiasFill"
                                            style={{ width: `${Math.max(2, Math.min(100, v * 100))}%` }}
                                        />
                                    </div>
                                    <span className="comboBiasValue">{formatComplexity(v)}</span>
                                </div>
                            );
                        })}
                    </div>
                </ComboSection>
            )}

            {spellbookUri && (
                <div className="comboCardBodyFooter">
                    <ActionButton
                        onPress={() => window.open(spellbookUri, '_blank', 'noopener')}
                        UNSAFE_className="comboSpellbookBtn"
                    >
                        <LinkOut />
                        <span>View on Commander Spellbook</span>
                    </ActionButton>
                </div>
            )}
        </div>
    );
}

function ComboSection({ title, hint, children }) {
    return (
        <section className="comboCardSection">
            <header className="comboCardSectionHeader">
                <span className="comboCardSectionTitle">{title}</span>
                {hint && <span className="comboCardSectionHint">{hint}</span>}
            </header>
            {children}
        </section>
    );
}

function ComboSubList({ label, tone, lines }) {
    return (
        <div className={`comboSubList comboSubList--${tone}`}>
            <span className="comboSubListLabel">{label}</span>
            <ul className="comboSubListItems">
                {lines.map((l, idx) => (
                    <li key={idx}>{l.parsed}</li>
                ))}
            </ul>
        </div>
    );
}

// ---------- Tiny helpers ---------------------------------------------------

function buildComboFlags(combo) {
    const flags = [];
    const cx = combo?.complexity || {};
    if (cx.requiresCardsOnBoard) {
        flags.push({
            key: 'board',
            label: 'Needs board state',
            tone: 'watch',
            hint: 'At least one piece must already be on the battlefield.',
        });
    }
    if (cx.requiresCommanderToBe) {
        flags.push({
            key: 'commander',
            label: `Commander: ${cx.requiresCommanderToBe}`,
            tone: 'cool',
            hint: 'Combo only works with this specific commander.',
        });
    }
    if (Array.isArray(cx.requiresKeywords) && cx.requiresKeywords.length > 0) {
        flags.push({
            key: 'keywords',
            label: `Needs ${cx.requiresKeywords.join(', ')}`,
            tone: 'watch',
            hint: 'Pieces of the combo need these keywords to function.',
        });
    }
    if (combo?.isManaSink) {
        flags.push({ key: 'manasink', label: 'Mana sink', tone: 'gold', hint: 'Acts as a sink for infinite mana.' });
    }
    if (combo?.templateMatched) {
        flags.push({
            key: 'template',
            label: 'Template match',
            tone: 'ok',
            hint: 'Detected via a generic pattern, not an exact Spellbook entry.',
        });
    }
    return flags;
}

function buildQualifierLabel(qualifiers) {
    if (!qualifiers) return '';
    const keys = Object.keys(qualifiers);
    if (!keys.length) return '';
    return keys
        .map((k) => k.replace(/^requires/, '').replace(/([A-Z])/g, ' $1').trim().toLowerCase())
        .join(' · ');
}

function buildQualifierHint(qualifiers) {
    const label = buildQualifierLabel(qualifiers);
    return label ? `Requires: ${label}` : '';
}

function humanizeSectionKey(key) {
    if (!key) return '';
    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (c) => c.toUpperCase())
        .replace(/\s+/g, ' ')
        .trim();
}

function computeComboStats(combos) {
    const counts = { WINCON: 0, ENABLER: 0, LOCK: 0 };
    const tagMap = new Map();
    const linchpinMap = new Map();
    let complexitySum = 0;
    let complexityN = 0;
    let cmcSum = 0;
    let cmcN = 0;

    for (const combo of combos) {
        counts[combo.__type] = (counts[combo.__type] || 0) + 1;

        for (const tag of combo.categories || []) {
            const key = getCategoryId(tag);
            if (!key) continue;
            tagMap.set(key, (tagMap.get(key) || 0) + 1);
        }

        for (const card of combo.__members || []) {
            if (!card) continue;
            const id = card.id || card.name;
            if (!id) continue;
            const existing = linchpinMap.get(id);
            if (existing) existing.count += 1;
            else linchpinMap.set(id, { card, count: 1 });
        }

        const cxFinal = combo?.complexity?.bias?.final;
        if (Number.isFinite(cxFinal)) {
            complexitySum += cxFinal;
            complexityN += 1;
        }

        const extra = combo?.complexity?.additionalCmcValue;
        if (Number.isFinite(extra)) {
            cmcSum += extra;
            cmcN += 1;
        }
    }

    const tagCounts = Array.from(tagMap.entries())
        .map(([tag, count]) => ({ tag, count }))
        .sort((a, b) => b.count - a.count);

    const linchpins = Array.from(linchpinMap.values())
        .filter((entry) => entry.count > 1)
        .sort((a, b) => b.count - a.count);

    return {
        total: combos.length,
        counts,
        tagCounts,
        linchpins,
        avgComplexity: complexityN ? complexitySum / complexityN : null,
        avgExtraCmc: cmcN ? cmcSum / cmcN : null,
    };
}
